// @ts-nocheck
import {
    useIsDistributorFormLoading,
    useSendBecomeADistributor,
} from "@store/Framework/Store/Bunzl/Components/BecomeADistributorForm/BecomeADistributorFormHooks";
import { BecomeADistributorModel } from "@store/Framework/Store/Bunzl/Components/BecomeADistributorForm/BecomeADistributorFormTypes";
import StoreFormWrapper from "@store/Library/Components/Foundation/StoreForm/StoreFormWrapper";
import InfosBox, { InfosBoxSize, InfosBoxType } from "@store/Library/Components/Toolkit/InfosBox";
import ReCaptcha from "@store/Overrides/Components/ReCaptcha";
import { contentSpacingStyles } from "@store/Themes/common/mixins/spacing";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { TextAreaPresentationProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Select, { SelectPresentationProps } from "@store/MobiusOverrides/Select/Select";
import availableCountriesField from "@store/Framework/Common/Fields/BecomeADistributorForm/availableCountriesField";
import availableStatesField from "@store/Framework/Common/Fields/BecomeADistributorForm/availableStatesField";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { siteWidthStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, FormEvent, RefObject, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";

interface StateModel {
    fields: {
        name: string;
        abbreviation: string;
    };
}

const enum fields {
    title = "title",
    text = "text",
    successMessage = "successMessage",
    availableStates = "availableStates",
    availableCountries = "availableCountries",
    validateReCaptcha = "validateReCaptcha",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.text]: string;
        [fields.successMessage]: string;
        [fields.availableStates]: StateModel[];
        [fields.availableCountries]: StateModel[];
        [fields.validateReCaptcha]: boolean;
    };
    submitTestSelector?: string; // todo
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const mapStateToProps = (state: ApplicationState) => ({
    session: state.context.session,
});

const mapDispatchToProps = {
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
};

interface BecomeADistributorFormStyles {
    textField?: TextFieldPresentationProps;
    textArea?: TextAreaPresentationProps;
    selectField?: SelectPresentationProps;
    formFieldsAligner?: InjectableCss;
    formFieldsWrapper?: InjectableCss;
    inputWrapper?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

const becomeADistributorFormStyles: BecomeADistributorFormStyles = {
    textArea: {
        css: css`
            &::placeholder {
                font-size: ${({ theme }) => theme.design.typography.size.tiny};
            }
        `,
    },
    selectField: {},
    formFieldsAligner: {
        css: css`
            width: 100%;
            max-width: ${pxToRem(500)};
        `,
    },
    formFieldsWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: ${pxToRem(40, 0)};
            background-color: ${getColor("common.accent")};
            margin-top: ${pxToRem(40)};
        `,
    },
    inputWrapper: {
        css: css`
            ${contentSpacingStyles(pxToRem(20), 0, pxToRem(20))}
        `,
    },
    submitButton: {
        color: "secondary",
    },
};

const styles = becomeADistributorFormStyles;

const StyledForm = getStyledWrapper("form");

const BecomeADistributorForm: FC<Props> = ({ fields, validateReCaptcha }) => {
    const formRef = useRef<HTMLFormElement>(null);
    const sendBecomeADistributor = useSendBecomeADistributor();
    const isLoading = useIsDistributorFormLoading();
    const [formError, setFormError] = useState<string>();
    const [isSuccess, setIsSuccess] = useState(false);
    const dispatch = useDispatch();
    const [formFields, setFields] = useState<BecomeADistributorModel>({
        firstName: undefined,
        lastName: undefined,
        companyName: undefined,
        address: undefined,
        suiteUnit: undefined,
        city: undefined,
        state: undefined,
        zip: undefined,
        country: undefined,
        companyPhone: undefined,
        email: undefined,
    });
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const handleFieldChange = <TValue,>(value: TValue, fieldKey: string) => {
        setFields(prevFields => ({
            ...prevFields,
            [fieldKey]: value,
        }));
    };

    const validateFields = (fields: string[]) => {
        const hasSomeFieldUndefined = fields.some(x => {
            return (formFields as any)[x] === undefined || (formFields as any)[x] === "";
        });

        return hasSomeFieldUndefined;
    };

    const onSubmitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (
            validateFields([
                "firstName",
                "lastName",
                "companyName",
                "address",
                "city",
                "state",
                "zip",
                "country",
                "companyPhone",
                "email",
            ])
        ) {
            return;
        }

        if (fields.validateReCaptcha) {
            const isReCaptchaValid = await validateReCaptcha({ location: "BecomeADistributor", recaptchaRef });

            if (!isReCaptchaValid) {
                return;
            }
        }

        sendBecomeADistributor(
            formFields,
            () => {
                setIsSuccess(true);
            },
            errorMessage => {
                setFormError(errorMessage);
            },
        );
    };

    return (
        <LoadingOverlay loading={isLoading}>
            {isSuccess ? (
                <InfosBox type="success" size="medium">
                    {fields.successMessage}
                </InfosBox>
            ) : (
                <StoreFormWrapper generalErrorMessage={formError}>
                    <StyledForm ref={formRef} onSubmit={onSubmitHandler}>
                        <StyledWrapper {...styles.formFieldsWrapper}>
                            <StyledWrapper {...styles.formFieldsAligner}>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="firstName"
                                        {...styles.textField}
                                        label={translate("First Name")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "firstName")}
                                        value={formFields.firstName}
                                        placeholder={translate("John")}
                                        autoComplete="firstName"
                                        required
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="lastName"
                                        {...styles.textField}
                                        label={translate("Last Name")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "lastName")}
                                        value={formFields.lastName}
                                        placeholder={translate("Doe")}
                                        autoComplete="lastName"
                                        required
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="companyName"
                                        name="companyName"
                                        {...styles.textField}
                                        label={translate("Company Name")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "companyName")}
                                        value={formFields.companyName}
                                        placeholder={translate("Example .INC")}
                                        autoComplete="companyName"
                                        required
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="address"
                                        name="address"
                                        {...styles.textField}
                                        label={translate("Address")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "address")}
                                        value={formFields.address}
                                        placeholder={translate("Address")}
                                        autoComplete="address"
                                        required
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="suiteUnit"
                                        name="Suite/Unit"
                                        {...styles.textField}
                                        label={translate("Suite/Unit")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "suiteUnit")}
                                        value={formFields.suiteUnit}
                                        placeholder={translate("Suite/Unit")}
                                        autoComplete="suiteUnit"
                                        required={false}
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="city"
                                        name="City"
                                        {...styles.textField}
                                        label={translate("City")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "city")}
                                        value={formFields.city}
                                        placeholder={translate("City")}
                                        autoComplete="City"
                                        required
                                    />
                                </StyledWrapper>
                                <StyledWrapper {...styles.inputWrapper}>
                                    <Select
                                        id="state"
                                        name="state"
                                        {...styles.selectField}
                                        label={translate("State")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "state")}
                                        value={formFields.state}
                                        placeholder={translate("State")}
                                        autoComplete="state"
                                        required
                                    >
                                        <option value="" selected disabled hidden>
                                            {translate("Select a state")}
                                        </option>
                                        {fields.availableStates?.map(x => (
                                            <option key={x.fields.abbreviation} value={x.fields.abbreviation}>
                                                {x.fields.name}
                                            </option>
                                        ))}
                                    </Select>
                                </StyledWrapper>

                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="zip"
                                        name="Zip"
                                        {...styles.textField}
                                        label={translate("Zip")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "zip")}
                                        value={formFields.zip}
                                        placeholder={translate("Zip")}
                                        autoComplete="Zip"
                                        required
                                    />
                                </StyledWrapper>

                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="email"
                                        {...styles.textField}
                                        label={translate("Email")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "email")}
                                        value={formFields.email}
                                        placeholder={translate("example@example.com")}
                                        autoComplete="email"
                                        required
                                    />
                                </StyledWrapper>

                                <StyledWrapper {...styles.inputWrapper}>
                                    <Select
                                        id="country"
                                        name="Country"
                                        {...styles.selectField}
                                        label={translate("Country")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "country")}
                                        value={formFields.country}
                                        placeholder={translate("Country")}
                                        autoComplete="country"
                                        required
                                    >
                                        <option value="" selected disabled hidden>
                                            {translate("Select a country")}
                                        </option>
                                        {fields.availableCountries?.map(x => (
                                            <option key={x.fields.abbreviation} value={x.fields.abbreviation}>
                                                {x.fields.name}
                                            </option>
                                        ))}
                                    </Select>
                                </StyledWrapper>

                                <StyledWrapper {...styles.inputWrapper}>
                                    <TextField
                                        id="companyPhone"
                                        name="CompanyPhone"
                                        {...styles.textField}
                                        label={translate("Phone")}
                                        onChange={e => handleFieldChange(e.currentTarget.value, "companyPhone")}
                                        value={formFields.companyPhone}
                                        placeholder={translate("0123456789")}
                                        autoComplete="phone"
                                        required
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                            {fields.validateReCaptcha && (
                                <StyledWrapper {...styles.inputWrapper}>
                                    <ReCaptcha recaptchaRef={recaptchaRef} />
                                </StyledWrapper>
                            )}

                            <StyledWrapper {...styles.inputWrapper}>
                                <Button
                                    {...styles.submitButton}
                                    onClick={e => {
                                        onSubmitHandler;
                                    }}
                                    data-test-selector="submit_BecomeADistributor"
                                >
                                    {translate("Submit")}
                                </Button>
                            </StyledWrapper>
                        </StyledWrapper>
                    </StyledForm>
                </StoreFormWrapper>
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BecomeADistributorForm),
    definition: {
        group: "Basic",
        displayName: "Become A Distributor Form",
        fieldDefinitions: [
            {
                name: fields.validateReCaptcha,
                displayName: "Validate reCaptcha.",
                editorTemplate: "CheckboxField",
                fieldType: "General",
                defaultValue: true,
                isRequired: false,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "",
            },
            {
                name: fields.successMessage,
                editorTemplate: "RichTextField",
                fieldType: "Translatable",
                defaultValue: "Thank you for considering Kishigo.",
            },
            {
                ...availableStatesField,
            },
            {
                ...availableCountriesField,
            },
        ],
    },
};

export default widgetModule;
